import FetchFacilities from '@Application/Facilities/Facilities/Queries/FetchFacilities';
import fetchFacilities from '@Application/Facilities/Facilities/fetchFacilities';
import EmptyState from '@Guidelines/Component/EmptyState';
import ErrorState from '@Guidelines/Component/ErrorState';
import FacilityCard from '@UI/Pages/Agencies/Facilities/FacilityCard';
import { openFacilitySidebar } from '@UI/Pages/Agencies/Facilities/Signals/facilitySidebarSignal';
import WithPermission from '@UI/Providers/PermissionProvider/WithPermission';
import { PermissionEnum } from '@Application/Security/Roles';
import useCurrentOrganization from '@UI/Providers/CurrentOrganizationProvider/useCurrentOrganization';
import type FacilityView from '@UI/Store/Facilities/Views/FacilityView';
import { css } from '@emotion/react';
import { Building2, Plus, TriangleAlert } from 'lucide-react';
import * as React from 'react';
import { useDispatch, useStoreQuery } from '@verclam/synapse';
import { Button, DataView as FacilitiesDataView, Skeleton } from '@verclam/ui-kit';

const skeletonGridStyle = css`
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(min(18rem, 100%), 1fr));
	row-gap: 0.875rem;
	column-gap: 1.25rem;
`;

const SKELETON_CARD_COUNT = 6;
const skeletonCardIndexes = [...Array(SKELETON_CARD_COUNT).keys()];

/**
 * The agencies of the current organization, as a card grid — no pagination, search, filter or
 * grouping, the API already scopes the list to one organization (`/api/organizations/{id}/facilities`)
 * and the volume stays in the tens. `AppMain` stops `<Outlet>` from mounting once a session exists but
 * no organization is resolved, so the `organizationId === null` branch below is a defensive second
 * line, not the primary gate — it is what keeps this page from ever building an unconstructable URL if
 * it is ever rendered standalone (e.g. in a test).
 */
const FacilitiesContent: React.FC = () => {
	const dispatch = useDispatch();
	const { currentOrganization } = useCurrentOrganization();
	const organizationId = currentOrganization?.id ?? null;

	// `FacilitiesListView` carries `organizationId`, and `stopOn` compares it against the organization
	// currently in scope: switching organizations from the header selector changes `organizationId`,
	// `stopOn` then reads `false` against the list cached for the previous one, and the query re-runs.
	// No `cache` config is used (the project default): the View itself carries the perimeter that
	// `stopOn` compares, so this is the "View portant le périmètre" strategy rather than `CACHE_DISABLED`.
	const {
		data: facilities,
		isLoading,
		refetch,
		error,
	} = useStoreQuery({
		selector: (state) => state.facilitiesStore.facilities,
		stopOn: (value) => !!value && value.organizationId === organizationId,
		query: () => {
			if (organizationId === null) {
				return Promise.resolve();
			}

			return dispatch(fetchFacilities(new FetchFacilities(organizationId)));
		},
		deps: [organizationId],
	});

	const handleCreateClick = React.useCallback(() => {
		openFacilitySidebar(null);
	}, []);

	const renderFacilityCard = React.useCallback(
		(facility: FacilityView) => <FacilityCard facility={facility} />,
		[],
	);

	const dataViewPt = React.useMemo(
		() => ({ grid: { style: { gridAutoRows: 'auto', rowGap: '0.875rem', columnGap: '1.25rem' } } }),
		[],
	);

	if (organizationId === null) {
		return (
			<EmptyState
				title="Aucune structure"
				description="Vous n’êtes affecté à aucune structure : aucune agence ne peut être chargée."
				icon={<TriangleAlert size={28} />}
			/>
		);
	}

	if (isLoading) {
		return (
			<div css={skeletonGridStyle}>
				{skeletonCardIndexes.map((index) => (
					<Skeleton key={index} height="11rem" animation="wave" shape="rectangle" />
				))}
			</div>
		);
	}

	if (facilities === null || error) {
		return (
			<ErrorState
				title="Impossible de charger les agences"
				description="Une erreur est survenue pendant le chargement de la liste des agences."
				onRetry={refetch}
			/>
		);
	}

	if (facilities.list.length === 0) {
		return (
			<EmptyState
				title="Aucune agence"
				description="Créez votre première agence pour commencer."
				icon={<Building2 size={28} />}
				action={
					<WithPermission permission={PermissionEnum.FACILITY_WRITE}>
						<Button
							type="button"
							label="Nouvelle agence"
							icon={<Plus size={16} />}
							onClick={handleCreateClick}
						/>
					</WithPermission>
				}
			/>
		);
	}

	return (
		<FacilitiesDataView
			value={facilities.list}
			layout="grid"
			dataKey="id"
			itemTemplate={renderFacilityCard}
			pt={dataViewPt}
		/>
	);
};

export default FacilitiesContent;
